import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import { get, omit, set } from "lodash";
import React from "react";
import { Form as FinalForm } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Col, Container, Row, Spinner } from "reactstrap";
import NumberFormatField from "../../components/Form/NumberFormatFieldV2/NumberFormatFieldV2";
import SingleSelectField from "../../components/Form/SingleSelectFieldV2/SingleSelectField";
import TextField from "../../components/Form/TextFieldV2/TextFieldV2";
import * as S from "../../constants/StringConstants";
import { IRootState } from "../../store/reducers";
import { fetchAffiliateOptions } from "../../store/reducers/affiliateBuilder";
import {
  createAffiliate,
  fetchAffiliate,
  hideErrorToastAffiliate,
  updateAffiliate,
} from "../../store/reducers/affiliates";
import "./AffiliateBuilder.scss";

import formatString from "format-string-by-pattern";
import AppBar from "src/components/AppBar/AppBar";
import Icon, { IconName } from "src/components/Icon/Icon";
import { Container as DetailContainer } from "../../components/Container/Container";
import DisTable from "../../components/TableV2/Table";
import Toast from "../../components/Toast/Toast";
import { deleteAffiliateLogo, fetchAffiliateLogo, uploadAffiliateLogo } from "../../store/reducers/affiliatesImages";
import { addDefaultOption, undefinedIfDefault } from "../../util/utils.defaultValues";
import * as P from "../../util/utils.validators";
import AffiliateQuotesWidget from "./AffiliateQuotesWidget";

interface IAffiliateBuilderProps extends StateProps, DispatchProps, RouteComponentProps<{ id: string }> {}

interface AffiliateBuilderState {
  logoUploading: boolean;
  uploadPercentage: number;
}

class AffiliateBuilder extends React.Component<IAffiliateBuilderProps, AffiliateBuilderState> {
  state = {
    logoUploading: false,
    uploadPercentage: 0,
  };

  validate = (values: { [x: string]: any; contacts: any[] }) => {
    if (Object.keys(values).length === 0) {
      return;
    }

    const errors = {};

    if (P.isEmpty(values.name)) {
      errors["name"] = S.FORM_FIELD_REQUIRED;
    }

    const zipCode = get(values, "address.zip");
    const zipPattern = P.ZIP_CODE_REGEX;
    if (zipCode && !zipPattern.test(zipCode)) {
      set(errors, "address.zip", S.FORM_FIELD_INVALID_ZIPCODE);
    }

    return errors;
  };

  handleSubmit = async (values: any) => {
    try {
      if (this.affiliateID) {
        await this.props.updateAffiliate(values);
      } else {
        if (values.affiliateID === 0) {
          delete values.affiliateID;
        }
        values.branchTypeID = undefinedIfDefault(values.branchTypeID);
        if (values.address) {
          values.address.stateID = undefinedIfDefault(values.address.stateID);
        }
        await this.props.createAffiliate(values);
      }
    } catch (err) {
      return { [FORM_ERROR]: "Error Submitting" };
    }

    this.props.history.push("/affiliates/all");
  };

  componentDidMount() {
    this.props.fetchAffiliateOptions();
    const id = this.affiliateID;
    if (id) {
      this.props.fetchAffiliate(id.toString());
      this.props.fetchAffiliateLogo(id);
    }
  }

  componentDidUpdate(prevProps: IAffiliateBuilderProps) {
    const prevId = parseInt(prevProps.match.params.id, 10);
    const id = this.affiliateID;
    if (id && id !== prevId) {
      this.props.fetchAffiliate(id.toString());
      this.props.fetchAffiliateLogo(id);
    }
  }

  get affiliateID() {
    const id = parseInt(this.props.match.params.id, 10);

    return id && !isNaN(id) ? id : null;
  }

  getContactTitle(contacts: any[], index: number) {
    const contact = contacts[index];
    return contact && contact.isPrimary ? S.AFF_DETAIL_CARD_TITLE_PRI : S.AFF_DETAIL_CARD_TITLE_AFF;
  }

  onAffiliateFileSelectionChange = ({ target }) => {
    const file: File | undefined = target?.files?.[0];
    if (!file || !this.affiliateID) return;

    const reset = () => {
      if (target) target.value = "";
    };

    const formData = new FormData();
    formData.append("file", file, file.name);

    this.setState({ logoUploading: true, uploadPercentage: 0 });

    const fileReader = new FileReader();
    fileReader.onload = e => {
      this.props.uploadAffiliateLogo(this.affiliateID!, formData, (percent: number) => {
        this.setState({ uploadPercentage: percent });
      });
    };
    fileReader.readAsDataURL(file);

    reset();
  };

  onAffiliateFileDelete(affiliateID: number) {
    if (!affiliateID) return;
    this.props.deleteAffiliateLogo(affiliateID);
  }

  render() {
    const { dropdownOptions, affiliates, affiliateImages } = this.props;
    const phoneMask = { name: "phone-1", parse: "(999) 999-9999" };

    const id = this.affiliateID;
    const affiliate = affiliates.map[id];

    const defaultValues = {
      branchTypeID: undefined,
      affiliateID: 0,
      externalID: 0,
      contacts: [
        {
          isPrimary: true,
        },
      ],
      address: {},
    };

    const companies = affiliate ? affiliate.companies : [];
    const branchList = addDefaultOption("", get(dropdownOptions, "branchList", [])) || [];
    const rbmList = addDefaultOption("", get(dropdownOptions, "rbmList", [])) || [];
    const stateList = addDefaultOption("", get(dropdownOptions, "selectStateList", []));

    const initialValues = affiliate ? omit(affiliate, ["companies", "selectBranchList"]) : defaultValues;

    const image = affiliateImages.images.find(x => x.affiliateID === this.affiliateID);
    const logoAffiliateUrl = image?.imageUrl;

    return (
      <div>
        <Toast message={S.LOADING} onClose={() => {}} open={this.props.loading} />
        <Toast
          message={affiliates.errorMessage}
          onClose={() => {
            this.props.hideErrorToastAffiliate();
          }}
          open={affiliates.error}
        />
        <Toast
          message={S.UPLOADING(this.state.uploadPercentage)}
          onClose={() => this.setState({ logoUploading: false })}
          open={this.state.logoUploading}
        />
        <React.Fragment>
          <AppBar icon={IconName.Affiliates} title={S.AFF_DETAIL_PAGE_TITLE} />

          <FinalForm
            onSubmit={this.handleSubmit}
            initialValues={initialValues}
            mutators={{ ...arrayMutators }}
            validate={this.validate}
            render={({ handleSubmit, submitting, submitError, invalid }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <div className="affiliate-builder__form">
                    <section className="affiliate-builder__wrap">
                      <DetailContainer>
                        <div className="affiliate-builder__detail-form">
                          <Col className="app-form-header">
                            <h4 className="form-title">{S.AFF_DETAIL_CARD_TITLE}</h4>
                          </Col>

                          <Row>
                            <Col sm="6">
                              <TextField label={S.AFF_NAME_LABEL} key="affiliate.name-key" name="name" />
                            </Col>
                            <Col sm="3">
                              <TextField
                                disabled={true}
                                label={S.AFF_AFFILIATE_ID_LABEL}
                                key="affiliate.affiliate-id-key"
                                name="affiliateID"
                              />
                            </Col>
                            <Col sm="3">
                              <NumberFormatField
                                label={S.AFF_EXTERNAL_ID_LABEL}
                                key="affiliate.external-id-key"
                                name="externalID"
                              />
                            </Col>
                          </Row>

                          <Row>
                            <Col sm="3">
                              <SingleSelectField
                                label={S.AFF_BRANCH_TYPE_LABEL}
                                key="affiliate.brancyTypeID-key"
                                name="branchTypeID"
                                options={branchList}
                              />
                            </Col>
                            <Col sm="3">
                              <SingleSelectField
                                label={S.AFF_RBM_LABEL}
                                key="affiliate.rbmId-key"
                                name="rbmId"
                                options={rbmList}
                              />
                            </Col>
                          </Row>

                          <Row>
                            <Col sm="3">
                              <TextField
                                label={S.AFF_EMAIL_LABEL}
                                key="affiliate.email-key"
                                name="email"
                                type="email"
                              />
                            </Col>
                            <Col sm="3">
                              <TextField
                                label={S.AFF_PHONE_LABEL}
                                key="affiliate.phone-key"
                                name="phoneNumber"
                                type="tel"
                                parse={formatString(phoneMask.parse)}
                              />
                            </Col>
                          </Row>

                          <Row>
                            <Col>
                              <FieldArray name="contacts">
                                {({ fields }) => {
                                  if (fields.length === 0) {
                                    fields.push({ isPrimary: true });
                                  }

                                  return (
                                    <Container className="bootsrap-container-override">
                                      {fields.map((name, index) => (
                                        <React.Fragment key={`contacts[${index}]`}>
                                          <Row>
                                            <Col>
                                              <div className="company-builder__card-title">
                                                <h4 className="form-title">
                                                  {(() => this.getContactTitle(fields.value, index))()}
                                                </h4>
                                              </div>
                                            </Col>
                                          </Row>
                                          <Row>
                                            <Col sm="3">
                                              <TextField
                                                key={`affiliate.contacts[${index}].firstName`}
                                                label={S.AFF_FIRST_NAME_LABEL}
                                                name={`${name}.firstName`}
                                              />
                                            </Col>
                                            <Col sm="3">
                                              <TextField
                                                key={`affiliate.contacts[${index}].lastName`}
                                                label={S.AFF_LAST_NAME_LABEL}
                                                name={`${name}.lastName`}
                                              />
                                            </Col>
                                            <Col className="d-flex align-items-center">
                                              {index === fields.length - 1 && (
                                                <button
                                                  type="button"
                                                  className="button-v2 button-v2__secondary-mini"
                                                  onClick={e => {
                                                    e.preventDefault();
                                                    fields.push({ isPrimary: false });
                                                  }}>
                                                  <span>{S.AFF_ADD_ALT_CONTACT}</span>
                                                </button>
                                              )}
                                            </Col>
                                            <Row>
                                              <Col sm="6" style={{ marginLeft: "21px" }}>
                                                {index !== 0 && (
                                                  <div className="affiliate-builder__delete-btn">
                                                    <button
                                                      type="button"
                                                      className="button-v2__medication-link-blue large"
                                                      onClick={() => fields.remove(index)}
                                                      aria-label="Delete contact row">
                                                      <span className="remove-contact-button__label">
                                                        {S.AFF_REMOVE_ALTERNATE_CONTACT}
                                                        <Icon name={IconName.RemoveMinusCircleBlue} size="20px" />
                                                      </span>
                                                    </button>
                                                  </div>
                                                )}
                                              </Col>
                                            </Row>
                                          </Row>
                                        </React.Fragment>
                                      ))}
                                    </Container>
                                  );
                                }}
                              </FieldArray>
                            </Col>
                          </Row>

                          <Row>
                            <Col>
                              <div className="company-builder__card-title">
                                <h4 className="form-title">{S.AFF_DETAIL_CARD_TITLE4}</h4>
                              </div>
                            </Col>
                          </Row>

                          <Row>
                            <Col sm="3">
                              <TextField
                                label={S.AFF_STREET_ADDRESS_LABEL}
                                key="affiliate.address-key"
                                name="address.streetAddress"
                              />
                            </Col>
                            <Col sm="3">
                              <TextField label={S.AFF_CITY_LABEL} key="affiliate.city-key" name="address.city" />
                            </Col>
                            <Col sm="3">
                              <SingleSelectField
                                label={S.AFF_STATE_LABEL}
                                key="affiliate.state-key"
                                name="address.stateID"
                                options={stateList}
                              />
                            </Col>
                            <Col sm="3">
                              <TextField label={S.AFF_ZIP_LABEL} key="affiliate.zip-key" name="address.zip" />
                            </Col>
                          </Row>

                          <Row>
                            <Col sm="6">
                              <TextField name="notes" key="affiliate.notes-key" label={S.AFF_NOTES_LABEL} multiline />
                            </Col>
                          </Row>

                          {companies && companies.length > 0 && (
                            <React.Fragment>
                              <Row>
                                <Col>
                                  <div className="company-builder__card-title">
                                    <h2 className="heading3">{S.AFF_DETAIL_CARD_TITLE5}</h2>
                                  </div>
                                </Col>
                              </Row>
                              <Row>
                                <Col className="affiliate-builder__table-override">
                                  <DisTable
                                    columns={columns}
                                    initialState={{}}
                                    isPaginated={false}
                                    onUpdate={async ({ page, rows, sort, query }) => {
                                      let mapped = (companies as any[]).map(c => {
                                        c.id = c.companyID;
                                        return c;
                                      });
                                      return {
                                        total: companies.length,
                                        data: mapped,
                                      };
                                    }}
                                  />
                                </Col>
                              </Row>
                            </React.Fragment>
                          )}

                          <Row>
                            <Col sm="3">
                              <span className="upload-logo-label">{S.AFF_UPLOAD_AFFILIATE_LOGO}</span>
                              <div className="logo-upload-wrapper">
                                <label className="logo-upload-container" htmlFor="affiliate-logo-upload">
                                  {logoAffiliateUrl ? (
                                    <img src={logoAffiliateUrl} alt="Affiliate Logo" className="logo-preview" />
                                  ) : (
                                    <div className="logo-placeholder">
                                      <Icon name={IconName.UploadCloud} size="24px" />
                                    </div>
                                  )}
                                </label>

                                {logoAffiliateUrl && (
                                  <div>
                                    <button
                                      type="button"
                                      className="button-v2 button-v2__primary tiny button-flex"
                                      onClick={() => this.onAffiliateFileDelete(affiliate.affiliateID)}>
                                      <span>{S.AFF_DELETE_AFFILIATE_LOGO}</span>
                                    </button>
                                  </div>
                                )}

                                <input
                                  id="affiliate-logo-upload"
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  onChange={this.onAffiliateFileSelectionChange}
                                />
                              </div>
                            </Col>
                          </Row>

                          <Row>
                            <Col>
                              <div className="affiliate-builder__top-bar">
                                <div className="buttons">
                                  <button
                                    className="button-v2 button-v2__secondary"
                                    onClick={() => this.props.history.goBack()}>
                                    {S.AFF_CANCEL_BUTTON}
                                  </button>

                                  <button className="button-v2 button-v2__primary" type="submit" disabled={submitting}>
                                    {submitting ? <Spinner color="light" /> : S.AFF_SUBMIT_BUTTON}
                                  </button>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </DetailContainer>
                    </section>
                    <aside className="affiliate-quotes-widget">
                      <AffiliateQuotesWidget />
                    </aside>
                  </div>
                </form>
              );
            }}
          />
        </React.Fragment>
      </div>
    );
  }
}

const columns = [
  { key: "name", title: S.AFF_COM_COL_COMPANY_NAME },
  { key: "branch", title: S.AFF_COM_COL_BRANCH },
  { key: "emailAddress", title: S.AFF_COM_COL_EMAIL },
  { key: "phoneNumber", title: S.AFF_COM_COL_PHONE },
];

const mapStateToProps = ({
  auth,
  affiliates: { loading: loadingAffiliate, ...affiliates },
  affiliateImages,
  affiliateBuilder: { loading: loadingOptions, ...dropdownOptions },
}: IRootState) => ({
  auth,
  affiliates,
  affiliateImages,
  dropdownOptions,
  loading: loadingAffiliate || loadingOptions || affiliateImages.loading,
});

const mapDispatchToProps = {
  createAffiliate,
  fetchAffiliateOptions,
  fetchAffiliate,
  updateAffiliate,
  deleteAffiliateLogo,
  fetchAffiliateLogo,
  uploadAffiliateLogo,
  hideErrorToastAffiliate,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(AffiliateBuilder);
